import React, { useMemo, useState } from "react";
import { Form } from "react-bootstrap";
import { FaBan, FaCalendarCheck, FaClock, FaMapMarkerAlt, FaUsers } from "react-icons/fa";
import {
  buildAvailabilitySessions,
  filterAvailabilityMonths,
} from "../utils/availabilitySessions";
import { formatSouthAfricaTime, SOUTH_AFRICA_TIME_LABEL } from "../utils/projectFormDateTime";

const sessionStatus = (session) => {
  if (session.past) return { icon: FaClock, text: "Ended" };
  if (session.full) return { icon: FaBan, text: "Full" };
  if (session.remaining !== null) {
    return {
      icon: FaUsers,
      text: `${session.remaining} of ${session.capacity} place${session.capacity === 1 ? "" : "s"} left`,
    };
  }
  return null;
};

// One chronological list of every session in the form, however many
// availability questions the leader split them across.
const AvailabilityPicker = ({ fields, answers, onToggle }) => {
  const [showPast, setShowPast] = useState(false);
  const [activity, setActivity] = useState("");

  const { months, activities, selectedCount, pastCount, upcomingCount } = useMemo(
    () => buildAvailabilitySessions(fields, answers),
    [fields, answers],
  );
  const visibleMonths = useMemo(
    () => filterAvailabilityMonths(months, { showPast, activity }),
    [months, showPast, activity],
  );

  return (
    <div className="session-picker">
      <div className="session-picker__toolbar">
        <span className="session-picker__count" aria-live="polite">
          <FaCalendarCheck aria-hidden="true" />
          {selectedCount} session{selectedCount === 1 ? "" : "s"} selected
        </span>
        {activities.length > 1 && (
          <Form.Select
            size="sm"
            aria-label="Filter sessions by activity"
            value={activity}
            onChange={(event) => setActivity(event.target.value)}>
            <option value="">All activities</option>
            {activities.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </Form.Select>
        )}
        {pastCount > 0 && (
          <Form.Check
            type="switch"
            id="session-picker-show-past"
            label={`Show ${pastCount} past session${pastCount === 1 ? "" : "s"}`}
            checked={showPast}
            onChange={(event) => setShowPast(event.target.checked)}
          />
        )}
      </div>

      {!visibleMonths.length && (
        <p className="session-picker__empty">
          {upcomingCount
            ? "No sessions match this filter."
            : "There are no upcoming sessions on this form yet."}
        </p>
      )}

      {visibleMonths.map((month) => (
        <section key={month.key} className="session-month" aria-label={month.label}>
          <h5>{month.label}</h5>
          {month.days.map((day) => (
            <div key={day.key} className="session-day">
              <div className="session-day__date">
                <span>{day.weekday}</span>
                <strong>{day.date}</strong>
              </div>
              <div className="session-day__list">
                {day.sessions.map((session) => {
                  const { slot } = session;
                  const disabled = (session.past && !session.selected) || session.full;
                  const status = sessionStatus(session);
                  const StatusIcon = status?.icon;
                  return (
                    <label
                      key={slot._id}
                      className={`session-option${session.selected ? " is-selected" : ""}${disabled ? " is-disabled" : ""}`}>
                      <input
                        type="checkbox"
                        checked={session.selected}
                        disabled={disabled}
                        onChange={() => onToggle(session.fieldId, slot._id)}
                      />
                      <span className="session-option__body">
                        <strong>
                          {formatSouthAfricaTime(slot.startTime)}–{formatSouthAfricaTime(slot.endTime)}{" "}
                          <small>{SOUTH_AFRICA_TIME_LABEL}</small> · {slot.label || slot.activity}
                        </strong>
                        {slot.location && (
                          <small>
                            <FaMapMarkerAlt aria-hidden="true" /> {slot.location}
                          </small>
                        )}
                      </span>
                      {status && (
                        <span className="session-option__status">
                          <StatusIcon aria-hidden="true" /> {status.text}
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </section>
      ))}
    </div>
  );
};

export default AvailabilityPicker;
